import React from 'react';
import type { CanvasSectionBlock } from '../sectionRegistry';
import {
  getMediaFrameImageClasses,
  getMediaFrameShellClasses,
  getMediaFrameViewportClasses,
  resolveMediaFrameVariant,
} from '../../../lib/elementStyleTokens';
import {
  buttonVisualStateToCssDeclarations,
  buttonVisualStateToReactStyle,
  normalizeButtonDesignStyle,
} from '../../../lib/buttonDesignStyles';
import ImageTransformLayer from '../imageEditing/ImageTransformLayer';
import { buildImageTransformStyle } from '../imageEditing/imageBlockEditing';

type WrapperProps = React.HTMLAttributes<HTMLDivElement> & {
  style?: React.CSSProperties;
};

type ImageInteraction = {
  blockId: string;
  kind: string;
} | null;

type ImageBlockRendererProps = {
  block: CanvasSectionBlock;
  blockWrapperProps: WrapperProps;
  selectableBlockHitProps: Record<string, string>;
  selectionTarget: string;
  renderSelectionOverlay: (block: CanvasSectionBlock) => React.ReactNode;
  fontLibrary: Record<string, string>;
  isAdmin: boolean;
  isHeroSlotBlock: boolean;
  heroSlotRole: string;
  isImageAdjustMode: boolean;
  activeAdjustBlockId?: string | null;
  activeImageInteraction: ImageInteraction;
  onExitImageAdjust?: () => void;
  beginSurfaceMove: (
    event: React.PointerEvent,
    blockId: string,
    style: Record<string, unknown> | undefined
  ) => void;
  moveSurfaceMove: (event: React.PointerEvent) => void;
  endSurfaceMove: (event: React.PointerEvent) => void;
  handleWheelZoom: (
    event: React.WheelEvent,
    blockId: string,
    style: Record<string, unknown> | undefined
  ) => void;
  beginHandleInteraction: (
    event: React.PointerEvent,
    blockId: string,
    style: Record<string, unknown> | undefined,
    handleKind: string,
    bounds: DOMRect
  ) => void;
  moveHandleInteraction: (event: React.PointerEvent) => void;
  endHandleInteraction: (event: React.PointerEvent) => void;
};

export function ImageBlockRenderer({
  block,
  blockWrapperProps,
  selectableBlockHitProps,
  selectionTarget,
  renderSelectionOverlay,
  fontLibrary,
  isAdmin,
  isHeroSlotBlock,
  heroSlotRole,
  isImageAdjustMode,
  activeAdjustBlockId,
  activeImageInteraction,
  onExitImageAdjust,
  beginSurfaceMove,
  moveSurfaceMove,
  endSurfaceMove,
  handleWheelZoom,
  beginHandleInteraction,
  moveHandleInteraction,
  endHandleInteraction,
}: ImageBlockRendererProps) {
  const src = String((block.content?.src as string) ?? '');
  const alt = String((block.content?.alt as string) ?? 'Section image');
  const imageFrame = resolveMediaFrameVariant(block.content?.frame);
  const isHeroBackgroundBlock = isHeroSlotBlock && heroSlotRole === 'background';
  const showHeroBackgroundImage = isHeroBackgroundBlock && isAdmin;
  const imageDesignStyle = normalizeButtonDesignStyle(block.style);
  const imageRegularStyle = buttonVisualStateToReactStyle(imageDesignStyle, fontLibrary);
  const imageHoverCss = buttonVisualStateToCssDeclarations(imageDesignStyle.hover, fontLibrary);
  const imageTransformStyle = buildImageTransformStyle(block.style as Record<string, unknown> | undefined);
  const imageDomId = `builder-image-${block.id}`;
  const imageAdjustActive = isImageAdjustMode && activeAdjustBlockId === block.id;
  const imageAdjustDragging =
    activeImageInteraction?.blockId === block.id && activeImageInteraction.kind === 'move';
  const imageAdjustInteractionKind =
    activeImageInteraction?.blockId === block.id ? activeImageInteraction.kind : null;

  return (
    <div key={block.id} {...blockWrapperProps}>
      {imageHoverCss ? (
        <style>{`[data-builder-image-id="${imageDomId}"]:hover { ${imageHoverCss} }`}</style>
      ) : null}
      <div
        data-builder-image-id={imageDomId}
        data-builder-allow-interaction={imageAdjustActive ? 'true' : undefined}
        {...(selectionTarget !== 'content' ? selectableBlockHitProps : {})}
        className={
          isHeroBackgroundBlock
            ? 'relative h-full w-full'
            : `${getMediaFrameShellClasses(imageFrame)} ${
                imageAdjustActive
                  ? 'touch-none select-none ring-1 ring-sky-300/80 shadow-[0_0_0_1px_rgba(56,189,248,0.25)]'
                  : ''
              }`
        }
        style={isHeroBackgroundBlock ? undefined : imageRegularStyle}
      >
        <div
          className={
            isHeroBackgroundBlock
              ? 'relative h-full w-full overflow-hidden'
              : getMediaFrameViewportClasses(imageFrame)
          }
        >
          {src && (!isHeroBackgroundBlock || showHeroBackgroundImage) ? (
            <img
              src={src}
              alt={alt}
              data-builder-editor-bounds="content"
              {...(selectionTarget === 'content' ? selectableBlockHitProps : {})}
              className={getMediaFrameImageClasses(imageFrame)}
              style={imageTransformStyle}
              loading="lazy"
              draggable={false}
            />
          ) : !isHeroBackgroundBlock ? (
            <div
              data-builder-editor-bounds="content"
              {...(selectionTarget === 'content' ? selectableBlockHitProps : {})}
              className="flex h-full w-full items-center justify-center text-xs text-stone-500"
            >
              Image block
            </div>
          ) : isAdmin ? (
            <div className="flex h-full w-full items-center justify-center bg-stone-900/30 text-xs font-medium tracking-wide text-white/80">
              Hero background
            </div>
          ) : null}
          {!isHeroBackgroundBlock ? (
            <div className="pointer-events-none absolute inset-0 rounded-lg bg-gradient-to-t from-black/15 to-transparent" />
          ) : null}
        </div>
        {imageAdjustActive ? (
          <ImageTransformLayer
            dragging={imageAdjustDragging}
            interactionKind={imageAdjustInteractionKind}
            onExit={onExitImageAdjust}
            onSurfacePointerDown={(event) =>
              beginSurfaceMove(event, block.id, block.style as Record<string, unknown> | undefined)
            }
            onSurfacePointerMove={moveSurfaceMove}
            onSurfacePointerUp={endSurfaceMove}
            onSurfaceWheel={(event) =>
              handleWheelZoom(event, block.id, block.style as Record<string, unknown> | undefined)
            }
            onHandlePointerDown={(event, handleKind, bounds) =>
              beginHandleInteraction(
                event,
                block.id,
                block.style as Record<string, unknown> | undefined,
                handleKind,
                bounds
              )
            }
            onHandlePointerMove={moveHandleInteraction}
            onHandlePointerUp={endHandleInteraction}
          />
        ) : null}
        {renderSelectionOverlay(block)}
      </div>
    </div>
  );
}
